import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiFileUpload, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  FILE_MIME_TYPE_EXTENSIONS,
  ICONS_WEB,
  INPUT_TYPES,
} from "@miplatform/constants";
import { BillingCommitmentType } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";
import { useAddPurchaseOrderModalViewModel } from "./useAddPurchaseOrderModalViewModel";

const purchaseOrderSchema = z.object({
  name: z.string().min(1, "Label is required."),
  orderTemplateId: z.string().min(1, "Related order is required."),
  type: z.nativeEnum(BillingCommitmentType, {
    errorMap: () => ({ message: "Payment method is required." }),
  }),
  number: z.string().min(1, "# is required."),
  amount: z.number(),
});

const BILLING_LABELS = {
  [BillingCommitmentType.PurchaseOrder]: "Purchase Order #",
  [BillingCommitmentType.Check]: "Check #",
  [BillingCommitmentType.CreditCard]: "Reference #",
  [BillingCommitmentType.Cash]: "Cash Receipt #",
};

export const AddPurchaseOrderModal = ({
  homeId,
  selectedPurchaseOrder,
  orderTemplates = [],
  open,
  onCreate = () => {},
  onClose,
  onPurchaseOrdersRefresh = () => {},
}) => {
  const theme = useTheme();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: {},
    resolver: zodResolver(purchaseOrderSchema),
  });

  const formBillingCommitmentType = watch("type");
  const formAmount = watch("amount");

  const {
    isLoading,
    file,
    documentCategory,
    getDocumentCategory,
    handleFileChange,
    resetFile,
    createPurchaseOrder,
    updatePurchaseOrder,
  } = useAddPurchaseOrderModalViewModel({
    homeId,
    selectedPurchaseOrder,
    onPurchaseOrdersRefresh,
    onCreate,
    onClose: () => {
      reset();
      onClose();
    },
  });

  const paymentMethodOptions = [
    { label: "Purchase Order", value: BillingCommitmentType.PurchaseOrder },
    { label: "Cash", value: BillingCommitmentType.Cash },
    { label: "Check", value: BillingCommitmentType.Check },
    { label: "Credit Card", value: BillingCommitmentType.CreditCard },
  ];

  const purchaseOrderFields = [
    {
      label: "Related Order",
      name: "orderTemplateId",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      options: orderTemplates.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
      handleChange: (_, option) => {
        if (option) {
          setValue("orderTemplateId", option.props.value, {
            shouldDirty: true,
          });
        }
      },
      width: "100%",
      disabled: orderTemplates.length === 1,
    },
    {
      label: "Payment Information Label",
      name: "name",
      required: true,
      formControl: control,
      width: "100%",
    },
    {
      label: "Payment Method",
      name: "type",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      options: paymentMethodOptions,
      width: "50%",
    },
    {
      label: "Amount",
      name: "amount",
      fieldType: INPUT_TYPES.CURRENCY,
      required: true,
      value: formAmount,
      formControl: control,
      allowNegative: true,
      width: "50%",
      setValue: (rawValue) => {
        if (rawValue === "-") {
          return;
        }

        const val = rawValue === "" ? null : Number(rawValue);

        setValue(`amount`, val, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },

    {
      label:
        BILLING_LABELS[formBillingCommitmentType] ||
        BILLING_LABELS[BillingCommitmentType.PurchaseOrder],
      name: "number",
      required: true,
      formControl: control,
      width: "50%",
    },
  ];

  const handleCancel = () => {
    reset();
    onClose();
    resetFile();
  };

  useEffect(() => {
    getDocumentCategory();
  }, []);

  useEffect(() => {
    if (orderTemplates.length === 1) {
      const [orderTemplate] = orderTemplates;
      setValue("orderTemplateId", orderTemplate.id);
    }
  }, [orderTemplates]);

  useEffect(() => {
    if (selectedPurchaseOrder) {
      setValue("name", selectedPurchaseOrder?.name || "");
      setValue("orderTemplateId", selectedPurchaseOrder?.orderTemplateId || "");
      setValue("number", selectedPurchaseOrder?.number || "");
      setValue("type", selectedPurchaseOrder?.type || "");
      setValue("amount", selectedPurchaseOrder?.amount ?? null, {
        shouldValidate: true,
      });
    } else {
      reset({});
    }
  }, [selectedPurchaseOrder]);

  return (
    <MiModal
      title={
        selectedPurchaseOrder
          ? "Edit Payment Information"
          : "Add Payment Information"
      }
      variant="temporary"
      open={open}
      loading={isLoading}
      closeModal={handleCancel}
      titleIcon={mdi[ICONS_WEB.DOCUMENT]}
      leftIconColor={theme.palette.primary.main}
      rightIcon={mdi[ICONS_WEB.CLOSE]}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: selectedPurchaseOrder
            ? "Edit Payment Information"
            : "Add Payment Information",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled:
            isLoading ||
            !isValid ||
            (!file && !selectedPurchaseOrder) ||
            (!isDirty && !file) ||
            (file && file?.error),
          onClick: handleSubmit(
            selectedPurchaseOrder ? updatePurchaseOrder : createPurchaseOrder
          ),
        },
      ]}
    >
      <MiDetailFields detailFields={purchaseOrderFields} />
      <Box px={2} pt={1}>
        <MiFileUpload
          dragAndDrop
          multiple={false}
          testId="mi-file-upload-payment-information"
          disabled={!documentCategory}
          validFileTypes={documentCategory?.allowedFileFormats}
          onChange={(files) => handleFileChange(files)}
          onDelete={resetFile}
          acceptableFormatText={`Acceptable formats: ${documentCategory?.allowedFileFormats
            .map((mimeType) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
            .join(", ")}`}
        />
      </Box>
    </MiModal>
  );
};
